import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Mail, Copy, Trash2, Check, Clock } from "lucide-react";

// Keep in lock-step with PUPPY_HIDABLE_SECTIONS on the backend.
const HIDABLE = [
  { key: "weights",       label: "Daily weight log",   help: "Birth-to-go-home weight readings."  },
  { key: "ens",           label: "ENS protocol",        help: "Days 3–16 stimulation observations." },
  { key: "socialization", label: "Socialization log",   help: "Rule-of-7s exposure entries."        },
  { key: "notes",         label: "Breeder's notes",     help: "Free-form notes you typed on the puppy." },
  { key: "markings",      label: "Markings details",    help: "Coat markings the breeder tracked."  },
  { key: "birth_order",   label: "Birth order",         help: "Position in the whelp sequence."     },
];

export default function InviteOwnerDialog({ puppy, open, onOpenChange }) {
  const [form, setForm] = useState({ owner_email: "", owner_name: "", message: "", hidden_sections: [] });
  const [invites, setInvites] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !puppy?.id) return;
    setForm({ owner_email: "", owner_name: "", message: "", hidden_sections: [] });
    loadInvites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, puppy?.id]);

  const loadInvites = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/puppies/${puppy.id}/invites`);
      setInvites(data.items || []);
    } catch (err) {
      // Past-invites is a non-critical sidebar — surface the error to the
      // console for triage but don't toast (would distract from the main form).
      // eslint-disable-next-line no-console
      console.debug(`loadInvites failed for puppy ${puppy.id}`, err);
    } finally { setLoading(false); }
  };

  const toggleHidden = (k) => setForm((f) => ({
    ...f,
    hidden_sections: f.hidden_sections.includes(k)
      ? f.hidden_sections.filter((x) => x !== k)
      : [...f.hidden_sections, k],
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.owner_email) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/puppies/${puppy.id}/invites`, form);
      toast.success("Invite sent");
      if (data?.signup_url) {
        // Stage the link in the clipboard for quick re-share.
        try { await navigator.clipboard?.writeText(data.signup_url); } catch { /* fine, optional */ }
      }
      setForm({ owner_email: "", owner_name: "", message: "", hidden_sections: [] });
      loadInvites();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send invite");
    } finally {
      setSubmitting(false);
    }
  };

  const revoke = async (invite) => {
    if (!window.confirm(`Revoke invite to ${invite.owner_email}? They won't be able to claim this puppy.`)) return;
    try {
      await api.delete(`/puppy-invites/${invite.id}`);
      toast.success("Invite revoked");
      loadInvites();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not revoke");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="invite-owner-dialog">        <DialogHeader>
          <DialogTitle className="font-display tracking-tighter text-xl">
            Invite {puppy?.name ? `${puppy.name}'s` : "the new owner"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2" data-testid="invite-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="invite-name" className="text-xs uppercase tracking-wide text-muted-foreground">New owner name</Label>
              <Input id="invite-name" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} className="mt-1.5" placeholder="Jane Doe" data-testid="invite-name" />
            </div>
            <div>
              <Label htmlFor="invite-email" className="text-xs uppercase tracking-wide text-muted-foreground">Email *</Label>
              <Input id="invite-email" type="email" required value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} className="mt-1.5" data-testid="invite-email" />
            </div>
          </div>

          <div>
            <Label htmlFor="invite-msg" className="text-xs uppercase tracking-wide text-muted-foreground">Personal message (optional)</Label>
            <Textarea id="invite-msg" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5" placeholder="Welcome to the family!" data-testid="invite-message" />
          </div>

          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Hide from the new owner</p>
            <p className="text-xs text-muted-foreground mb-3">
              Tick anything you&apos;d rather keep private. Your original records stay intact.
            </p>
            <div className="space-y-2">
              {HIDABLE.map((h) => (
                <label key={h.key} className="flex items-start gap-3 p-2 rounded-md border border-border hover:bg-muted/40 cursor-pointer transition-colors" data-testid={`invite-hide-${h.key}`}>
                  <input
                    type="checkbox"
                    checked={form.hidden_sections.includes(h.key)}
                    onChange={() => toggleHidden(h.key)}
                    className="mt-0.5 h-4 w-4 accent-primary cursor-pointer"
                    data-testid={`invite-hide-checkbox-${h.key}`}
                  />
                  <span className="text-sm">
                    <span className="font-medium block">{h.label}</span>
                    <span className="text-xs text-muted-foreground">{h.help}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="invite-cancel">Cancel</Button>
            <Button type="submit" disabled={submitting} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="invite-submit">
              <Mail size={14} className="mr-1.5" /> {submitting ? "Sending…" : "Send invite"}
            </Button>
          </DialogFooter>
        </form>

        {/* Outstanding invites for this puppy */}
        {(invites.length > 0 || loading) && (
          <div className="mt-6 pt-5 border-t border-border" data-testid="invite-list">
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-3">Past invites</p>
            {loading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : (
              <ul className="space-y-2">
                {invites.map((inv) => (
                  <li key={inv.id} className="flex items-start justify-between gap-3 p-3 border border-border rounded-md" data-testid={`invite-row-${inv.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{inv.owner_email}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5">
                        {inv.status === "claimed" ? <Check size={12} /> : inv.status === "revoked" ? <Trash2 size={12} /> : <Clock size={12} />}
                        <span className="capitalize">{inv.status}</span>
                        {inv.created_at ? <span>· {new Date(inv.created_at).toLocaleDateString()}</span> : null}
                        {inv.hidden_sections?.length > 0 && <span>· hides {inv.hidden_sections.length}</span>}
                      </p>
                    </div>
                    {inv.status === "pending" && (
                      <button onClick={() => revoke(inv)} className="p-1.5 hover:bg-muted rounded-md text-muted-foreground hover:text-destructive shrink-0" data-testid={`invite-revoke-${inv.id}`} title="Revoke">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
